import { FormGroup } from '@angular/forms';
import { inject, Injectable } from '@angular/core';
import { SmartToastService } from '@shared/services/smart-toast.service';
import { BaseSocialeWarningsService } from './warnings/base-sociale.warnings';
import { BaseSocialeFieldValidators } from './validators/base-sociale.field-validators';
import { baseSocialeFormValidators } from './validators/base-sociale.form-validators';
import { CoopValidators } from '@core/helpers/validators.helper';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { BaseSocialeFormListeners } from './listeners/base-sociale.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { setInitialChiusuraEsercizio, setInitialTipoSezEnte } from '../../helpers/form-store.helpers';
import { formPaths } from '@shared/constants/form-paths.const';
import { buildContrattiSociFormArray, buildContrattiUsatiSociLavoratoriFormArray } from './common/builders/base-sociale-form-array.builder';

@Injectable()
export class BaseSocialeFormService extends BaseRevisioneFormService<BaseSocialeWarningsService, BaseSocialeFieldValidators> {
    protected smartToast = inject(SmartToastService);

    constructor(protected validators: BaseSocialeFieldValidators, protected warningsService: BaseSocialeWarningsService) {
        super(warningsService, validators);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Totali
            'my_TotSociPersoneFisiche',
            'my_TotSociPerSessoEta',
            'my_TotSociSessoProvenienza',
            'my_TotLavoratoriSoci',
            'my_TotLavoratoriNonSoci',

            // Stato pratica
            'my_StatoVerbale',
            'my_StatoAccertamento',
            'my_DBPresente',
            'my_IdAssegnazione',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,
            `${formPaths.datiIdentificativi}.my_Anno1`,
            `${formPaths.datiIdentificativi}.my_TipoSezEnte`,

            // Amministrazione controllo
            `${formPaths.ammControllo}.my_CkNorme`,
            `${formPaths.ammControllo}.my_CalcObbOrganoContr`,
            `${formPaths.ammControllo}.my_NormeSpaDualistico.my_CkSpdCoop9soci`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop9soci`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop20soci`,
            `${formPaths.ammControllo}.my_NormeSpaOrdinario.my_CkSpoCoop9soci`,
            `${formPaths.ammControllo}.my_NormeSpaMonistico.my_CkSpmCoop9soci`,

            // Raggiungimento scopo sociale
            `${formPaths.scopoSociale}.my_RicaviVendAnno1Dep`,
            `${formPaths.scopoSociale}.my_RicaviVendDep`,

            // Conclusioni
            `${formPaths.conclusioni}.my_CkProposte`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_DataApprovazione2`,
            `${formPaths.datiBilancio}.my_DataApprovazione1`,
            `${formPaths.datiBilancio}.my_TipoSchemaBil2`,
            `${formPaths.datiBilancio}.my_TipoSchemaBil1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1`,
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_BilAnno1`,

            // Bilancio
            `${formPaths.bilancio}.my_BilancioAllegato`,
            `${formPaths.bilancio}.my_AnnoPrimoEs`,
            `${formPaths.bilancio}.my_AnnoSecondoEs`,
            `${formPaths.bilancio}.my_CkEnteControllaSocObbRevCont`,
            `${formPaths.bilancio}.my_CkRedazBilConsol`,
            `${formPaths.bilancio}.my_CkPrestitoSoci`,
            `${formPaths.bilancio}.my_TotConfSociFin`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_PatrimonioBil`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_PatrNettoUltBil`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_PrestitoSociale`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_CICR`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_NumSociDataDich`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_DataChiusEser`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_NSociPrestitoPF`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_PrestSociFruttPF`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_PrestSociInfruttPF`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_NSociPrestitoPG`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_PrestSociFruttPG`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_ValoreImmBil`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_ValoreImmICI`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_PrestSociInfruttPG`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_CkRispettatoLimiteIndivid`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_CkRipettatoCICR`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_lblSociPrestito`,
            `${formPaths.bilancio}.my_SchedaVerificaPrestito.my_lblImportoPrestito`,

            // Contributo biennale
            `${formPaths.contributoBiennale}.my_ContribPreCaricato`,

            // Scheda coop sociali
            `${formPaths.schedaCoopSociali}.my_ScSocObbl`,
            `${formPaths.schedaCoopSociali}.my_CkSociVolontari`,
            `${formPaths.schedaCoopSociali}.my_SociVolontari`,
        ]);
    }

    override initListener(): void {
        new BaseSocialeFormListeners(this._form, this.store, this.smartToast).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_TotSociPersoneFisiche: '',
                my_TotSociPerSessoEta: '',
                my_TotSociSessoProvenienza: '',
                my_TotLavoratoriSoci: '',
                my_TotLavoratoriNonSoci: '',
                my_StatoVerbale: '',
                my_StatoAccertamento: '',
                my_DBPresente: '',
                my_IdAssegnazione: '',
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                        my_Anno1: '',
                        my_TipoSezEnte: '',
                    }),

                    my_AmministrazioneControllo: this.fb.group({
                        my_CkNorme: '',
                        my_CalcObbOrganoContr: '',
                        my_NormeSpaDualistico: this.fb.group({
                            my_CkSpdCoop9soci: '',
                        }),
                        my_NormeSrl: this.fb.group({
                            my_CkSrlCoop9soci: '',
                            my_CkSrlCoop20soci: '',
                        }),
                        my_NormeSpaOrdinario: this.fb.group({
                            my_CkSpoCoop9soci: '',
                        }),
                        my_NormeSpaMonistico: this.fb.group({
                            my_CkSpmCoop9soci: '',
                        }),
                    }),

                    my_RaggScopoSociale: this.fb.group({
                        my_RicaviVendAnno1Dep: '',
                        my_RicaviVendDep: '',
                    }),

                    my_Conclusioni: this.fb.group({
                        my_CkProposte: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_DataApprovazione2: '',
                        my_DataApprovazione1: '',
                        my_TipoSchemaBil2: '',
                        my_TipoSchemaBil1: '',
                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtTOT2: '',
                            my_StPaAtTOT1: '',
                        }),
                        my_BilAnno2: '',
                        my_BilAnno1: '',
                    }),

                    my_Bilancio: this.fb.group({
                        my_BilancioAllegato: '',
                        my_AnnoPrimoEs: '',
                        my_AnnoSecondoEs: '',
                        my_CkEnteControllaSocObbRevCont: '',
                        my_CkRedazBilConsol: '',
                        my_CkPrestitoSoci: '',
                        my_TotConfSociFin: '',
                        my_SchedaVerificaPrestito: this.fb.group({
                            my_PatrimonioBil: '',
                            my_PatrNettoUltBil: '',
                            my_PrestitoSociale: '',
                            my_CICR: '',
                            my_NumSociDataDich: '',
                            my_DataChiusEser: '',
                            my_NSociPrestitoPF: '',
                            my_PrestSociFruttPF: '',
                            my_PrestSociInfruttPF: '',
                            my_NSociPrestitoPG: '',
                            my_PrestSociFruttPG: '',
                            my_ValoreImmBil: '',
                            my_ValoreImmICI: '',

                            my_PrestSociInfruttPG: '',
                            my_CkRispettatoLimiteIndivid: '',
                            my_CkRipettatoCICR: '',
                            my_lblSociPrestito: '',
                            my_lblImportoPrestito: '',
                        }),
                    }),

                    my_ContributoBiennale: this.fb.group({
                        my_ContribPreCaricato: '',
                    }),

                    my_SchedaCoopSociali: this.fb.group({
                        my_ScSocObbl: '',
                        my_CkSociVolontari: '',
                        my_SociVolontari: '',
                    }),

                    my_EffettivitaBaseSociale: this.fb.group({
                        my_CkProcedConforme: ['', CoopValidators.required(REQUIRED_STATUS)],
                        my_SpecProcedConforme: '',
                        my_ChiusuraEsercizio: ['', CoopValidators.required(REQUIRED_STATUS)],
                        my_CkReqSociCoerenti: ['', CoopValidators.required(REQUIRED_STATUS)],
                        my_SpecReqSociCoerenti: '',
                        my_CkSociSpecLimite: '',
                        my_CkSociSpecDiritti: '',
                        my_CtrlLavoratoriSoci: '',
                        my_CtrlLavoratoriNonSoci: '',
                        my_SocSpecNota: '',
                        my_CkNormeSocioLavorat: ['', CoopValidators.required],
                        my_CkRedazRegolam: '',
                        my_DataApprovAssemb: '',
                        my_DataDeposUfficio: '',
                        my_ChkRegolamentoCertificato: '',
                        my_CkContrattoCollLavSub: '',
                        my_CkRegolSvolgLav: '',
                        my_CkRegolNormeNonSub: '',
                        my_CkRegolAssPianoCrisi: '',
                        my_CkRegolAssPiano2: '',
                        my_CkRegolAvvNuove: '',
                        my_CkCoopTraPro: '',
                        my_NoteRegNonConforme: '',
                        my_NoteDataDeposUfficio: '',
                        my_CkSocLavSub: null,
                        my_NumSocLavSub: null,
                        my_CkSocLavSubTempoDeterminato: null,
                        my_NumSocLavSubTempoDeterminato: null,
                        my_CkSocLavParaSub: null,
                        my_NumSocLavParaSub: null,
                        my_CkSocLavAut: null,
                        my_NumSocLavAut: null,
                        // InfoPath: the "altro" checkbox is always disabled
                        my_CkSocLavAltro: { value: null, disabled: true },
                        my_NumSocLavAltro: null,
                        my_SociTechAmmini: null,
                        my_SociTechAmminiLav: null,
                        my_CkTipiRappLavCorretti: null,
                        my_MotTipiRappLavCorretti: null,
                        my_CtrlContrattiAdottatiSociLav: '',
                        my_DescContrSocLavNoConf: '',
                        my_DescRuoloSocNoAttSoc: '',
                        my_DescVarBaseSoc: '',
                        my_ckAttivFacchinaggioEsclus: null,
                        my_NoteNatMutEnte: [null, CoopValidators.required(REQUIRED_STATUS)],
                        my_SociFisCoopData: ['', this.validatorsService.my_SociFisCoopData],
                        my_SociGiuCoopData: ['', this.validatorsService.my_SociGiuCoopData],
                        my_SociFisFinData: ['', this.validatorsService.my_SociFisFinData],
                        my_SociGiuFinData: ['', this.validatorsService.my_SociGiuFinData],
                        my_SociSpecData: '',
                        my_Data1EsPrec: '',
                        my_SociFisCoopPrimo: '',
                        my_SociGiuCoopPrimo: '',
                        my_SociFisFinPrimo: '',
                        my_SociGiuFinPrimo: '',
                        my_SociSpecPrimo: '',
                        my_Data2EsPrec: '',
                        my_SociFisCoopSecondo: '',
                        my_SociGiuCoopSecondo: '',
                        my_SociFisFinSecondo: '',
                        my_SociGiuFinSecondo: '',
                        my_SociSpecSecondo: '',
                        my_gr0001: this.fb.group({
                            my_SociSpecData_personeFisiche: '',
                        }),
                        my_CtrlSociSessoEta: '',
                        my_CtrlSociProvenienza: '',
                        my_NumMprima: '',
                        my_NumFprima: '',
                        my_NumMseconda: '',
                        my_NumFseconda: '',
                        my_NumMterza: '',
                        my_NumFterza: '',
                        my_NumSociMComunitari: '',
                        my_NumSociFComunitari: '',
                        my_NumSociMExtracomunitari: '',
                        my_NumSociFExtracomunitari: '',
                        my_UnitaSociUtil: '',
                        my_UnitaNonSociUtil: ['', CoopValidators.required],
                        my_NumLavSub: '',
                        my_NumLavSubTempoDeterminato: ['', CoopValidators.required],
                        my_NumLavParaSub: ['', CoopValidators.required],
                        my_NumLavAut: ['', CoopValidators.required],
                        my_NumLavAltro: ['', CoopValidators.required],
                        my_ContrattoLavAltro: '',
                        my_UnitaSociUtilPrimo: '',
                        my_UnitaNonSociUtilPrimo: '',
                        my_NumLavSubPrimo: '',
                        my_NumLavSubTempoDeterminatoPrimo: '',
                        my_NumLavParaSubPrimo: '',
                        my_NumLavAutPrimo: '',
                        my_NumLavAltroPrimo: '',
                        my_UnitaSociUtilSecondo: '',
                        my_UnitaNonSociUtilSecondo: '',
                        my_NumLavSubSecondo: '',
                        my_NumLavSubTempoDeterminatoSecondo: '',
                        my_NumLavParaSubSecondo: '',
                        my_NumLavAutSecondo: '',
                        my_NumLavAltroSecondo: '',
                        my_ObbligoRilevazioneLavPrimo: '',
                        my_ObbligoRilevazioneLavSecondo: '',
                        my_ContrattiUsati: this.fb.group({
                            my_ElContrattiUsati: this.fb.array([]),
                        }),
                        my_CkUtilizzaContrattiSecondoLivello: '',
                        my_CkUtilizzaContrattiSecondoLivelloAziendali: '',
                        my_CkUtilizzaContrattiSecondoLivelloTerrProvinciale: '',
                        my_CkUtilizzaContrattiSecondoLivelloTerrRegionale: '',
                        my_CkContrattiSecondoLivelloRegFiscaleAgevolato: '',
                        my_NumSociMLavoratoriComunitari: ['', CoopValidators.required],
                        my_NumSociMLavoratoriExtracomunitari: ['', CoopValidators.required],
                        my_NumSociFLavoratoriComunitari: ['', CoopValidators.required],
                        my_NumSociFLavoratoriExtracomunitari: ['', CoopValidators.required],
                        my_NumNonSociMLavoratoriComunitari: ['', CoopValidators.required],
                        my_NumNonSociMLavoratoriExtracomunitari: ['', CoopValidators.required],
                        my_NumNonSociFLavoratoriComunitari: ['', CoopValidators.required],
                        my_NumNonSociFLavoratoriExtracomunitari: ['', CoopValidators.required],
                        my_ULAPrimo: '',
                        my_ULASecondo: '',
                        my_gruppo9: this.fb.group({
                            my_CkTipiRappLavConSociCorretti: null,
                        }),
                        my_gruppo11: this.fb.group({
                            my_CkContrApplicConforme: null,
                        }),
                        my_gruppo13: this.fb.group({
                            my_ckAttivFacchin: null,
                            my_ckAttivFacchinaggio: null,
                        }),
                        my_gruppo15: this.fb.group({
                            my_CkAlboImpreseArtigiane: null,
                        }),
                        my_gruppo17: this.fb.group({
                            my_ckSoggLibrSociNoAttSociali: null,
                        }),
                        my_gruppo20: this.fb.group({
                            my_CkRegolamentoConforme: '',
                        }),
                        my_ContrattiUsatiSociLavoratori: this.fb.group({
                            my_ElContrattiUsatiSociLavoratori: this.fb.array([]),
                        }),
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: baseSocialeFormValidators,
            }
        );
    }
    protected override beforePopulateForm(myVerbale: MyVerbale): void {
        buildContrattiSociFormArray(this.fb, myVerbale, this._form);
        buildContrattiUsatiSociLavoratoriFormArray(this.fb, myVerbale, this._form);
        setInitialTipoSezEnte(myVerbale, this.store);
        setInitialChiusuraEsercizio(myVerbale, this.store);
    }
}
